import type { DemographicFormData } from '@/components/annotation';

export interface ParticipantProfile {
  id: string;
  userId: string;
  age?: number;
  ageRange?: string;
  musicalGenres: string[];
  musicalEngagement: 'casual' | 'enthusiast' | 'musician' | 'professional';
  formalTrainingYears: number;
  listeningDevice: string;
  listeningContext: 'alone' | 'with-others';
  environment: string;
  aiMusicExperience: string;
  email?: string;
  annotation_count?: number;
  reward_eligible?: boolean;
  reward_claimed?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateParticipantResponse {
  participantId: string;
  message: string;
}

export interface GetParticipantResponse {
  participant: ParticipantProfile;
}

class ParticipantService {
  private baseUrl = '/api/participants';

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = localStorage.getItem('auth-token');
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Create a new participant profile
   */
  async createParticipant(data: DemographicFormData): Promise<CreateParticipantResponse> {
    return this.makeRequest<CreateParticipantResponse>('', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get participant profile by ID
   */
  async getParticipant(id: string): Promise<GetParticipantResponse> {
    return this.makeRequest<GetParticipantResponse>(`/${id}`);
  }

  /**
   * Get current user's participant profile
   */
  async getMyProfile(): Promise<GetParticipantResponse> {
    return this.makeRequest<GetParticipantResponse>('/me');
  }

  /**
   * Check if current user has a participant profile
   */
  async hasProfile(): Promise<boolean> {
    try {
      await this.getMyProfile();
      return true;
    } catch (error) {
      // If we get a 404 or "not found" error, the profile doesn't exist
      if (error instanceof Error && (
        error.message.includes('404') || 
        error.message.includes('not found') ||
        error.message.includes('Participant profile not found')
      )) {
        return false;
      }
      // For other errors, just return false instead of throwing
      console.warn('Error checking participant profile:', error);
      return false;
    }
  }

  /**
   * Update participant email
   */
  async updateEmail(email: string): Promise<{ message: string; email: string }> {
    return this.makeRequest<{ message: string; email: string }>('/me', {
      method: 'PATCH',
      body: JSON.stringify({ email }),
    });
  }

  /**
   * Get participant reward status with actual listening percentage
   */
  async getRewardStatus(): Promise<{
    email: string | null;
    annotationCount: number;
    rewardEligible: boolean;
    rewardClaimed: boolean;
    rewardClaimedAt: string | null;
    avgListeningPercentage: number;
    meetsListeningRequirement: boolean;
    isFullyEligible: boolean;
    listeningRequirement: number;
    top2ListeningPercentages: number[];
  }> {
    return this.makeRequest('/me/reward-status');
  }
}

export const participantService = new ParticipantService();